import { useMemo } from 'react';
import { LayoutGrid } from 'lucide-react';
import { SYMPHONY_LOOP_MARKS } from '../../../SymphonyMark';
import { connectorLogoUrl } from '../../../../lib/connectors';
import { GENOME_SHOWN_CHANNELS, GENOME_SUPPORTED_CHANNELS, genomeToolOverflowCount } from '../../../../lib/connectors-genome';
import { CONNECTORS_PAGE, type AppsController } from '../../../../lib/use-connectors';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useAlfredInOutreachEnabled } from '../../../../lib/alfred-in-outreach-flag';
import { useTranslation } from '../../../../lib/i18n';
import { ClaudeColorGlyph, WhatsAppColorGlyph, WixWordmark } from '../BrandGlyphs';
import styles from './mobile.module.css';

/**
 * The Connectors hero — the latest native design (Figma 13619-121017 / `ConnectorsHeroView`), ported
 * so mobile web matches iOS + Android:
 *
 *   a GREEN Symphony app-icon hub with two dashed concentric rings; **channels ring the TOP half**
 *   (WhatsApp, Claude, Apps & Web) with a "+N" overflow, **tools ring the BOTTOM half** (Gmail,
 *   Calendar, Wix) with a "+N" overflow. Every badge is a **grey circular icon-button** (42px) carrying
 *   a brand mark and a 12px Wix-Madefor label on its outer side; the two "+N" are **black circles** with
 *   a 16px light label. A CONNECTED badge gets a solid ink spoke, a disconnected one a dotted grey spoke.
 *   Once the badges have flown out of the hub, light runs the wires forever — INTO the hub on channels,
 *   OUT of it on tools.
 *
 * The shown badges are a FIXED showcase (matching native's `channelSpecs`/`toolSpecs`), not the account's
 * arbitrary connectors; the fetched `connectors` drive only the Gmail/Calendar connected state and the
 * tools "+N" count. Coordinates are VERBATIM from the flattened native geometry export (335×375 frame),
 * so all three surfaces draw the identical diagram. Like native the hero is decorative: only the two "+N"
 * chips act (they open the Tools / Channels tab), the rest is display-only.
 */

/* The native design box (Figma 335×375). */
const VW = 335;
const VH = 375;
const HUB = { x: 167.6, y: 187.4 };
const MARK_SIZE = 58;
/* Icon-button plate radius (diameter 42 — the Figma `.Icon Button`). */
const BADGE_R = 21;

/* Motion (mirrors native ConnectorsHeroView): the badges fly out of the hub (staggered spring), then —
   once the whole entrance has settled (ARRIVAL_MS) — a dot runs each wire forever. */
const ARRIVAL_MS = 1250;
const PULSE_STAGGER_MS = 240;

/* Channels folded into the top "+N" — imported from the shared genome module (`GENOME_SHOWN_CHANNELS`/
   `GENOME_SUPPORTED_CHANNELS`) rather than a local copy: this file used to keep its own duplicate, and it
   went stale (5) the moment Wearables made the desktop genome and both native platforms 6 — a same-diff
   parity break across web viewports alone. Importing removes the second copy that can drift. */
const SHOWN_CHANNELS = GENOME_SHOWN_CHANNELS;
const SUPPORTED_CHANNELS = GENOME_SUPPORTED_CHANNELS;

/* The two dashed rings, concentric on the hub — (rx, ry) verbatim from the export. */
const RINGS = [
  { rx: 90.4711, ry: 90.9851 },
  { rx: 64.7691, ry: 64.255 },
];

/* Badge centres (px = pt in the 335×375 frame). */
const CENTRES: Record<string, { x: number; y: number }> = {
  C0: { x: 51.6, y: 89.5 }, // WhatsApp
  C1: { x: 126.7, y: 43.5 }, // Claude
  C2: { x: 200.0, y: 45.6 }, // Apps & Web
  CX: { x: 278.1, y: 75.5 }, // channels +N
  T0: { x: 41.5, y: 305.6 }, // Gmail
  T1: { x: 128.1, y: 319.5 }, // Calendar
  T2: { x: 203.5, y: 319.4 }, // Wix
  TX: { x: 290.1, y: 307.6 }, // tools +N
};

/* Each spoke as its 7 control points — two cubic segments sharing the middle point. VERBATIM from the
   flattened export; the travelling dot rides this exact curve. */
const SPOKES: Record<string, number[][]> = {
  C0: [[52.3125, 108.213], [87.7295, 156.009], [107.165, 128.891], [138.847, 156.009], [146.368, 162.448], [153.116, 177.37], [154.141, 188.611]],
  C1: [[128.359, 55.2703], [118.966, 120.103], [152.193, 112.242], [155.877, 153.781], [156.752, 163.643], [156.761, 187.752], [158.119, 196.192]],
  C2: [[196.422, 52.0344], [209.856, 116.151], [176.201, 110.386], [175.125, 152.075], [174.87, 161.972], [176.37, 186.034], [175.543, 194.543]],
  CX: [[275.5, 90.4998], [260.5, 130.5], [234.988, 114.236], [203.307, 141.354], [195.785, 147.792], [189.038, 162.715], [188.012, 173.956]],
  T0: [[48, 292.233], [78.695, 218.276], [102.351, 250.326], [138.075, 221.241], [146.557, 214.336], [153.983, 198.119], [154.933, 185.821]],
  T1: [[128.359, 320.871], [118.966, 256.038], [152.193, 263.899], [155.877, 222.36], [156.752, 212.498], [156.761, 188.389], [158.119, 179.949]],
  T2: [[201.422, 324.106], [214.856, 259.989], [181.201, 265.755], [180.125, 224.066], [179.87, 214.169], [181.37, 190.107], [180.543, 181.598]],
  TX: [[190.5, 212], [217.022, 280.59], [241.306, 245.881], [272.393, 273.678], [279.773, 280.277], [288.684, 295.094], [289.466, 306.354]],
};

type Mark = { glyph?: React.ReactNode; logoUrl?: string; wix?: boolean };

interface HeroNode {
  id: string;
  intoHub: boolean;
  label: string;
  connected: boolean;
  kind: 'badge' | 'overflow';
  mark?: Mark;
  overflowCount?: number;
  overflowTab?: 'tools' | 'channels';
}

const dist2 = (p: number[]) => (p[0] - HUB.x) ** 2 + (p[1] - HUB.y) ** 2;
/** True when the spoke's hub end is p6 (rather than p0) — a couple are authored hub→badge. */
const hubEndIsP6 = (pts: number[][]) => dist2(pts[6]) <= dist2(pts[0]);
const spokePath = (p: number[][]) =>
  `M${p[0][0]},${p[0][1]} C${p[1][0]},${p[1][1]} ${p[2][0]},${p[2][1]} ${p[3][0]},${p[3][1]} C${p[4][0]},${p[4][1]} ${p[5][0]},${p[5][1]} ${p[6][0]},${p[6][1]}`;

/**
 * The Connectors hero (native `ConnectorsHeroView`, ported).
 */
export function ConnectorsGenomeMobile({
  controller,
  onBrowseTools,
  onBrowseChannels,
}: {
  controller: AppsController;
  onBrowseTools: () => void;
  onBrowseChannels: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();

  const connectors = controller.state.kind === 'ready' ? controller.state.connectors : [];
  const alfredOnOutreach = useAlfredInOutreachEnabled();

  const nodes: HeroNode[] = useMemo(() => {
    const toolConnected = (toolkit: string) => connectors.some((c) => c.toolkit === toolkit && c.connected);
    const googleCalConnected = connectors.some(
      (c) => (c.calendarProvider?.toUpperCase() === 'GOOGLE' || c.toolkit === 'googlecalendar') && c.connected,
    );

    // Channels (TOP arc). Connected → solid spoke: Claude always, WhatsApp reads as reachable, Apps &
    // Web is not a per-account connection. Mirrors native `channelSpecs`.
    const channels: HeroNode[] = [
      { id: 'C0', intoHub: true, label: t('connectors.channel.whatsapp.short'), connected: true, kind: 'badge', mark: { glyph: <g transform="translate(-10,-10)"><WhatsAppColorGlyph size={20} /></g> } },
      { id: 'C1', intoHub: true, label: t('connectors.hero.claude'), connected: true, kind: 'badge', mark: { glyph: <g transform="translate(-11,-11)"><ClaudeColorGlyph size={22} /></g> } },
      { id: 'C2', intoHub: true, label: t('connectors.hero.appsWeb'), connected: false, kind: 'badge', mark: { glyph: <g transform="translate(-10,-10)"><LayoutGrid size={20} /></g> } },
    ];
    const channelOverflow = Math.max(0, SUPPORTED_CHANNELS - SHOWN_CHANNELS);
    if (channelOverflow > 0) {
      channels.push({ id: 'CX', intoHub: true, label: '', connected: false, kind: 'overflow', overflowCount: channelOverflow, overflowTab: 'channels' });
    }

    // Tools (BOTTOM arc): Gmail + Calendar render their real (colored) connector logos; Wix is the mono
    // wordmark. Connected → solid spoke when the tool is connected; Wix always. Mirrors native `toolSpecs`.
    const tools: HeroNode[] = [
      { id: 'T0', intoHub: false, label: t('connectors.hero.gmail'), connected: toolConnected('gmail'), kind: 'badge', mark: { logoUrl: connectorLogoUrl('gmail') } },
      { id: 'T1', intoHub: false, label: t('connectors.hero.calendar'), connected: googleCalConnected, kind: 'badge', mark: { logoUrl: connectorLogoUrl('googlecalendar') } },
      { id: 'T2', intoHub: false, label: t('connectors.hero.wix'), connected: true, kind: 'badge', mark: { wix: true } },
    ];
    const toolOverflow = genomeToolOverflowCount(connectors, alfredOnOutreach);
    if (toolOverflow > 0) {
      tools.push({ id: 'TX', intoHub: false, label: '', connected: false, kind: 'overflow', overflowCount: toolOverflow, overflowTab: 'tools' });
    }

    return [...channels, ...tools];
  }, [connectors, t, alfredOnOutreach]);

  const onOverflow = (node: HeroNode) => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'genome_overflow', action: node.overflowTab });
    if (node.overflowTab === 'tools') onBrowseTools();
    else onBrowseChannels();
  };

  return (
    <div className={styles.genome}>
      <svg className={styles.genomeSvg} viewBox={`0 0 ${VW} ${VH}`} role="group" aria-label={t('connectors.title')}>
        {RINGS.map((r, i) => (
          <ellipse key={i} className={styles.hRing} cx={HUB.x} cy={HUB.y} rx={r.rx} ry={r.ry} />
        ))}

        {nodes.map((node) => (
          <Spoke key={`s-${node.id}`} node={node} />
        ))}
        {nodes.map((node) => (
          <Pulse key={`p-${node.id}`} node={node} stagger={nodes.indexOf(node)} />
        ))}

        <HubMark />

        {nodes.map((node, i) => (
          <Badge key={node.id} node={node} stagger={i} onOverflow={onOverflow} />
        ))}
      </svg>
    </div>
  );
}

/** The green Symphony app-icon hub: a lime squircle with the dark loop marks. */
function HubMark() {
  const rx = MARK_SIZE * 0.3;
  return (
    <g>
      <rect className={styles.hHubTile} x={HUB.x - MARK_SIZE / 2} y={HUB.y - MARK_SIZE / 2} width={MARK_SIZE} height={MARK_SIZE} rx={rx} />
      <g transform={`translate(${HUB.x},${HUB.y}) scale(${MARK_SIZE / 24}) translate(-12,-12)`}>
        {SYMPHONY_LOOP_MARKS.map((m, i) => (
          <rect key={i} className={styles.hHubMark} x={m.cx - m.size / 2} y={m.cy - m.size / 2} width={m.size} height={m.size} rx={m.rx} />
        ))}
      </g>
    </g>
  );
}

function Spoke({ node }: { node: HeroNode }) {
  const pts = SPOKES[node.id];
  if (!pts) return null;
  return (
    <path
      className={`${styles.hSpoke} ${node.connected ? styles.hSpokeOn : styles.hSpokeOff}`}
      d={spokePath(pts)}
      strokeDasharray={node.connected ? undefined : '2 2'}
    />
  );
}

/** The travelling signal: channels flow INTO the hub, tools flow OUT of it. */
function Pulse({ node, stagger }: { node: HeroNode; stagger: number }) {
  const pts = SPOKES[node.id];
  if (!pts) return null;
  const p6 = hubEndIsP6(pts);
  // offset-distance runs 0%→100% along p0→p6; pick the direction so the flow matches native.
  const out = (node.intoHub && p6) || (!node.intoHub && !p6);
  return (
    <circle
      className={`${styles.hPulse} ${out ? styles.hPulseOut : styles.hPulseIn}`}
      r={3}
      style={{ offsetPath: `path('${spokePath(pts)}')`, animationDelay: `${ARRIVAL_MS + stagger * PULSE_STAGGER_MS}ms` } as React.CSSProperties}
    />
  );
}

/** The mark inside a badge — a brand glyph (mono ink), a real connector logo (colored), or the Wix
 *  wordmark — centred on the badge. Nested groups so each mark sits at (0,0). */
function BadgeMark({ node }: { node: HeroNode }) {
  if (node.kind === 'overflow') {
    return (
      <text className={styles.hOverflowText} textAnchor="middle" dominantBaseline="central">
        {`+${node.overflowCount ?? 0}`}
      </text>
    );
  }
  const mark = node.mark;
  if (mark?.logoUrl) {
    const box = 22;
    return (
      <image
        href={mark.logoUrl}
        x={-box / 2}
        y={-box / 2}
        width={box}
        height={box}
        preserveAspectRatio="xMidYMid meet"
        onError={(e) => e.currentTarget.remove()}
      />
    );
  }
  if (mark?.wix) {
    const w = 24;
    return <g transform={`translate(${-w / 2},${-(w * 11.1995) / 28.9271 / 2})`}>{<WixWordmark width={w} />}</g>;
  }
  // Each glyph is already wrapped in a centring <g> at the call site (its own size differs).
  if (mark?.glyph) return <>{mark.glyph}</>;
  return null;
}

function Badge({ node, stagger, onOverflow }: { node: HeroNode; stagger: number; onOverflow: (n: HeroNode) => void }) {
  const c = CENTRES[node.id];
  if (!c) return null;
  const overflow = node.kind === 'overflow';
  const below = c.y > HUB.y; // tools sit below the hub → label under the badge
  const fly = {
    '--fx': `${(HUB.x - c.x) * 0.6}px`,
    '--fy': `${(HUB.y - c.y) * 0.6}px`,
    '--d': `${180 + stagger * 74}ms`,
  } as React.CSSProperties;

  const interactive = overflow
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `+${node.overflowCount ?? 0}`,
        onClick: () => onOverflow(node),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOverflow(node);
          }
        },
      }
    : {};

  return (
    <g transform={`translate(${c.x},${c.y})`} className={overflow ? styles.hBadgeTap : undefined} {...interactive}>
      {/* Inner group carries the fly-in; the outer <g> owns POSITION (an SVG attribute a transform
          keyframe would otherwise discard). */}
      <g className={styles.hBadgeFly} style={fly}>
        <circle className={overflow ? styles.hOverflowChip : styles.hPlate} cx={0} cy={0} r={BADGE_R} />
        <BadgeMark node={node} />
        {node.label && (
          <text className={styles.hLabel} textAnchor="middle" y={below ? BADGE_R + 14 : -(BADGE_R + 8)}>
            {node.label}
          </text>
        )}
      </g>
    </g>
  );
}
